import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../../stores/auth.store';
import { Icon } from '../../../components/ui/Icon';
import { AnimatedNumber } from '../../../components/ui/AnimatedNumber';
import { SkeletonKPIRow } from '../../../components/ui/Skeleton';
import { SectionMark } from '../../../components/ui/SectionMark';
import { ActionsMenu, type ActionItem } from '../../../components/ui/ActionsMenu';
import { useUrlState } from '../../../hooks/useUrlState';
import { fmtEur } from '../../../lib/utils';
import { BANCA_VISTE, type BancaVista } from '../../../lib/adminRoutes';
import { canWriteAdmin } from '../adminApi';
import { RiconciliazioneTab } from '../RiconciliazioneTab';
import { ChiuseQuiTab } from '../ChiuseQuiTab';
import { StoricoNote, useStorico, useSyncAll, useVista } from '../shared/AdminShell';
import { bancaSintesiQuery, fmtSaldo, parseEuro, quandoAggiornata, useBancaActions, type BancaSintesi } from './bancaApi';
import { BancaMobile } from './BancaMobile';
import { MovimentiGiorni } from './MovimentiGiorni';
import { useIsMobile } from '../../../hooks/useIsMobile';
import '../admin.css';
import '../fatture/fatture.css';
import './banca.css';

/**
 * Banca (`/admin/banca`) — cosa è passato sul conto, e quali bonifici vanno
 * ancora abbinati alle fatture.
 *
 *   intestazione  la frase calcolata: quanti bonifici tornano al centesimo con
 *                 una fattura, quanti vanno controllati, a che ora è la banca
 *   fascia        i singoli conti (principale, IVA, contanti, gli altri). Il
 *                 TOTALE non c'è: vive nella Situazione ("Soldi disponibili
 *                 oggi"), qui si vedono le parti (regola R2)
 *   viste         Bonifici da abbinare (predefinita) · Movimenti del conto ·
 *                 Da comunicare al commercialista, con il pallino dove c'è
 *                 qualcosa da fare
 *
 * Le chiavi di `?vista=` restano quelle dello scheletro (controllare,
 * movimenti, commercialista): cambiano solo le parole a schermo. Il filtro
 * "Anche prima del 2026" vale solo in Movimenti del conto.
 */
/**
 * Sul telefono la pagina è BancaMobile (l'estratto conto: un gruppo per giorno,
 * i gesti nel pannello del movimento). Sul computer resta questa.
 */
export function BancaPage() {
  const isMobile = useIsMobile();
  return isMobile ? <BancaMobile /> : <BancaDesktop />;
}

/**
 * Banca sul computer, ridisegnata il 26/10/2026 (os-planning/ridisegno/CAPITOLATO.md).
 *
 * La domanda: quanto c'è sul conto, cosa è entrato e cosa è uscito?
 * La forma: come un'app della banca. In cima la risposta in una frase (quanto si può ancora
 * spendere col fido Qonto); i conti come schede (si toccano per vedere solo i loro movimenti);
 * una riga "da fare" solo quando ci sono bonifici da abbinare; poi i movimenti a giorni coi loghi.
 *
 * Alleggerita ("troppe cose inutili", Lorenzo 26/10): via le tre linguette e la fascia che andava
 * a capo. "Bonifici da abbinare" (`?vista=controllare`) e "Da comunicare al commercialista"
 * (`?vista=commercialista`) restano com'erano, si aprono dalla riga "da fare" o dal menu ⋯.
 * Nei movimenti: via le date dal/al, la scelta del conto in tendina e l'ordinamento (vedi
 * MovimentiGiorni.tsx).
 */
function BancaDesktop() {
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteAdmin(user);
  const [vista, setVista] = useVista<BancaVista>(BANCA_VISTE);
  const [accountId, setAccountId] = useUrlState<string>('acc', '');
  const storico = useStorico();
  const sync = useSyncAll();
  const { data: s, isLoading } = useQuery(bancaSintesiQuery);
  const daAbbinare = s ? s.bonifici.sicuri + s.bonifici.incerti : 0;
  const principale = vista === 'movimenti';

  const menu: ActionItem[] = [
    { key: 'controllare', label: `Bonifici da abbinare a una fattura${daAbbinare ? ` (${daAbbinare})` : ''}`, icon: 'link', onClick: () => setVista('controllare'), hidden: vista === 'controllare' },
    { key: 'commercialista', label: `Pagamenti da comunicare al commercialista${s?.commercialista ? ` (${s.commercialista})` : ''}`, icon: 'file', onClick: () => setVista('commercialista'), hidden: vista === 'commercialista' },
    { ...storico.menuItem, hidden: !principale },
  ];
  const nomeConto = !accountId || !s ? null
    : s.principale?.id === accountId ? 'conto principale'
    : s.iva?.id === accountId ? `conto ${s.iva.nome}`
    : 'conto scelto';

  return (
    <div className="page adm-page ft-page bn-page">
      <header className="ft-hd">
        <div className="ft-hd-main">
          <SectionMark icon="swap" className="ft-sez" />
          <div className="min-w-0">
            <h1 className="ft-title">Banca</h1>
            {principale ? (
              <p className="ft-risposta"><Risposta s={s} /></p>
            ) : (
              <p className="ft-risposta">
                <a className="ft-indietro" onClick={() => setVista('movimenti')}><Icon name="arrowLeft" size={12} /> Movimenti del conto</a>
                <span className="ft-dove">{vista === 'controllare' ? 'Bonifici da abbinare a una fattura' : 'Pagamenti da comunicare al commercialista'}</span>
              </p>
            )}
          </div>
        </div>
        <div className="ft-hd-azioni">
          {sync.button}
          <ActionsMenu variant="ghost" label="Altre liste della banca" items={menu} />
        </div>
      </header>

      {principale && storico.on && <StoricoNote year={storico.year} onOff={() => storico.set(false)} />}

      {principale && (
        <>
          {isLoading && !s ? <SkeletonKPIRow count={4} withIcon={false} /> : s && (
            <Conti s={s} canWrite={canWrite} scelto={accountId} onScegli={(id) => setAccountId(accountId === id ? '' : id)} />
          )}
          {daAbbinare > 0 && (
            <button type="button" className="bn-dafare" onClick={() => setVista('controllare')}>
              <span className="bn-dafare-n">{daAbbinare}</span>
              <span className="min-w-0">
                <span className="t">{daAbbinare === 1 ? 'Un bonifico da abbinare a una fattura' : `${daAbbinare} bonifici da abbinare a una fattura`}</span>
                <span className="s">
                  {s!.bonifici.sicuri > 0
                    ? `${s!.bonifici.sicuri === 1 ? 'Uno torna' : `${s!.bonifici.sicuri} tornano`} al centesimo: basta confermare.`
                    : 'Tornano solo in parte: guardali e conferma quello giusto.'}
                </span>
              </span>
              <span className="bn-dafare-cta">Abbina <Icon name="chevronR" size={12} /></span>
            </button>
          )}
          <MovimentiGiorni canWrite={canWrite} showHistory={storico.on} nomeConto={nomeConto} onTuttiIConti={() => setAccountId('')} />
        </>
      )}
      {vista === 'controllare' && <RiconciliazioneTab canWrite={canWrite} />}
      {vista === 'commercialista' && <ChiuseQuiTab />}
    </div>
  );
}

/** La risposta: quanto si può ancora spendere col fido, e a che ora è la banca. Il saldo sta nella scheda del conto. */
function Risposta({ s }: { s: BancaSintesi | undefined }) {
  if (!s) return <>Leggo i conti…</>;
  if (!s.qontoCollegato) {
    return <span className="text-warn">Qonto non è collegato: senza, qui non arrivano né i saldi né i movimenti. Si collega dalle Impostazioni.</span>;
  }
  const fido = s.fido ?? 5000;
  const spendibile = (s.principale?.saldo ?? 0) + fido;
  return (
    <>
      {s.principale && (spendibile > 0
        ? <>Col fido di Qonto possiamo ancora spendere <strong>€ {fmtEur(spendibile, true)}</strong> dal conto principale.</>
        : <span className="is-alert">Il conto principale è oltre il fido di Qonto: le uscite possono essere bloccate.</span>)}
      {s.aggiornataAlle && <> Banca aggiornata {quandoAggiornata(s.aggiornataAlle)}.</>}
    </>
  );
}

/** I conti come schede: il principale e quello dell'IVA si toccano per vedere solo i loro movimenti. */
function Conti({ s, canWrite, scelto, onScegli }: { s: BancaSintesi; canWrite: boolean; scelto: string; onScegli: (id: string) => void }) {
  return (
    <section className="bn-conti2" aria-label="I conti">
      {s.principale && (
        <button type="button" className={`bn-conto${scelto === s.principale.id ? ' is-on' : ''}`} aria-pressed={scelto === s.principale.id} onClick={() => onScegli(s.principale!.id)}>
          <span className="k">Conto principale</span>
          <span className={`v${s.principale.saldo < 0 ? ' is-neg' : ''}`}>€ {fmtSaldo(s.principale.saldo)}</span>
          <span className="s">Qonto{s.principale.ibanFinale ? `, IBAN che finisce con ${s.principale.ibanFinale}` : ''}</span>
        </button>
      )}
      {s.iva && (
        <button type="button" className={`bn-conto${scelto === s.iva.id ? ' is-on' : ''}`} aria-pressed={scelto === s.iva.id} onClick={() => onScegli(s.iva!.id)}>
          <span className="k">Conto {s.iva.nome}</span>
          <span className="v">€ {fmtSaldo(s.iva.saldo)}</span>
          <span className="s">i soldi messi da parte per l'IVA</span>
        </button>
      )}
      {s.contanti && <ContoContanti saldo={s.contanti.saldo} canWrite={canWrite} />}
      {s.altri.nomi.length > 0 && (
        <div className="bn-conto is-fermo">
          <span className="k">Altri conti Qonto</span>
          <span className="v">€ {fmtSaldo(s.altri.saldo)}</span>
          <span className="s">{elenco(s.altri.nomi)}</span>
        </div>
      )}
    </section>
  );
}

/** I contanti si contano a mano: la matita apre il campo al posto della cifra. */
function ContoContanti({ saldo, canWrite }: { saldo: number; canWrite: boolean }) {
  const { correggiContanti } = useBancaActions();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const apri = () => { setValue(String(saldo).replace('.', ',')); setEditing(true); };
  const salva = () => {
    const n = parseEuro(value);
    if (!Number.isFinite(n)) return;
    setEditing(false);
    if (Math.round(n * 100) !== Math.round(saldo * 100)) correggiContanti.mutate(n);
  };
  return (
    <div className="bn-conto is-fermo">
      <span className="k">Contanti in cassa</span>
      {editing ? (
        <span className="bn-cassa-edit">
          <input className="inp inp-sm mono" inputMode="decimal" autoFocus aria-label="Contanti in cassa oggi, in euro" value={value}
            onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') salva(); if (e.key === 'Escape') setEditing(false); }} />
          <button className="btn btn-sm btn-primary btn-icon" aria-label="Salva i contanti" title="Salva i contanti" onClick={salva}><Icon name="check" size={12} /></button>
          <button className="btn btn-sm btn-ghost btn-icon" aria-label="Lascia i contanti come sono" title="Lascia i contanti come sono" onClick={() => setEditing(false)}><Icon name="x" size={12} /></button>
        </span>
      ) : (
        <span className="v">€ {fmtSaldo(saldo)}</span>
      )}
      <span className="s">
        {canWrite && !editing
          ? <button type="button" className="bn-correggi" disabled={correggiContanti.isPending} onClick={apri}><Icon name="pencil" size={10} /> Correggi i contanti</button>
          : 'si contano a mano'}
      </span>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⚠️ Da qui in giù i pezzi di prima del 26/10/2026 (la frase lunga e la fascia dei conti che
// andava a capo): non si mostrano più, restano per poterli rimettere.
// La frase in cima (R6): solo conteggi e un'ora, mai importi (R1)
// ════════════════════════════════════════════════════════════════════

/**
 * "Con i dati di oggi 1 pagamento in banca torna al centesimo con una fattura:
 * basta confermarlo. Altri 2 tornano solo in parte e vanno controllati. Banca
 * aggiornata oggi alle 10:45."
 *
 * Sta in uno <span> e non come testo nudo: sul telefono l'intestazione tiene
 * solo i figli elemento (mobile.md 4bis.3), e questa frase è un fatto, non prosa.
 */
function Frase({ s }: { s: BancaSintesi | undefined }) {
  if (!s) return null;
  if (!s.qontoCollegato) {
    return <span className="text-warn">Qonto non è collegato: senza, qui non arrivano né i saldi né i movimenti. Si collega dalle Impostazioni.</span>;
  }
  const { sicuri, incerti } = s.bonifici;
  const uno = (n: number, a: string, b: string) => (n === 1 ? a : b);
  let testo: string;
  if (sicuri > 0) {
    testo = `Con i dati di oggi ${sicuri} ${uno(sicuri, 'pagamento', 'pagamenti')} in banca ${uno(sicuri, 'torna', 'tornano')} al centesimo con una fattura: basta ${uno(sicuri, 'confermarlo', 'confermarli')}.`;
    if (incerti > 0) {
      testo += incerti === 1
        ? ' Un altro torna solo in parte e va controllato.'
        : ` Altri ${incerti} tornano solo in parte e vanno controllati.`;
    }
  } else if (incerti > 0) {
    testo = `Con i dati di oggi nessun pagamento in banca torna al centesimo con una fattura; ${incerti} ${uno(incerti, 'torna', 'tornano')} solo in parte e ${uno(incerti, 'va controllato', 'vanno controllati')}.`;
  } else {
    testo = 'Con i dati di oggi ogni pagamento in banca che somiglia a una fattura è già collegato: non ci sono bonifici da abbinare.';
  }
  // Il numero sta nel blocco in cima alla vista: qui solo che è successo (R1).
  if ((s.automatici ?? 0) > 0) testo += ' I pagamenti che tornavano del tutto si sono segnati da soli: li trovi in cima all’elenco.';
  if (s.aggiornataAlle) testo += ` Banca aggiornata ${quandoAggiornata(s.aggiornataAlle)}.`;
  return <span className="bn-frase">{testo}</span>;
}

// ════════════════════════════════════════════════════════════════════
// La fascia dei conti: le parti, mai il totale
// ════════════════════════════════════════════════════════════════════

function FasciaConti({ s, canWrite }: { s: BancaSintesi; canWrite: boolean }) {
  return (
    <div className="stat-band bn-conti">
      {s.principale && (
        <Cella
          label="Conto principale"
          value={s.principale.saldo}
          sub={s.principale.ibanFinale ? `Qonto, l’IBAN finisce con ${s.principale.ibanFinale}` : 'Qonto'}
        />
      )}
      {s.iva && <Cella label={`Conto ${s.iva.nome}`} value={s.iva.saldo} sub="Qonto, i soldi messi da parte per l’IVA" />}
      {s.contanti && <CellaContanti saldo={s.contanti.saldo} canWrite={canWrite} />}
      {s.altri.nomi.length > 0 && (
        <Cella label="Altri conti Qonto" value={s.altri.saldo} sub={elenco(s.altri.nomi)} />
      )}
    </div>
  );
}

/** "GoDeal e Salvadanaio", "A, B e C". */
function elenco(nomi: string[]): string {
  if (nomi.length <= 1) return nomi[0] ?? '';
  return `${nomi.slice(0, -1).join(', ')} e ${nomi[nomi.length - 1]}`;
}

function Cella({ label, value, sub }: { label: string; value: number; sub: string }) {
  return (
    <div>
      <div className="st-label">{label}</div>
      <div className="st-value"><span className="u">€</span><AnimatedNumber value={value} format={fmtSaldo} /></div>
      <div className="st-delta">{sub}</div>
    </div>
  );
}

/**
 * I contanti non arrivano da nessuna banca: si contano a mano. La matita apre
 * il campo al posto della cifra; si scrive quanto c'è in cassa OGGI e l'API
 * sposta il saldo di partenza di quanto serve (admin.banca.routes.ts).
 */
function CellaContanti({ saldo, canWrite }: { saldo: number; canWrite: boolean }) {
  const { correggiContanti } = useBancaActions();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const apri = () => { setValue(String(saldo).replace('.', ',')); setEditing(true); };
  const salva = () => {
    const n = parseEuro(value);
    if (!Number.isFinite(n)) return;
    setEditing(false);
    if (Math.round(n * 100) !== Math.round(saldo * 100)) correggiContanti.mutate(n);
  };
  return (
    <div>
      <div className="st-label">Contanti in cassa</div>
      {editing ? (
        <div className="bn-cassa-edit">
          <input
            className="inp inp-sm mono"
            inputMode="decimal"
            autoFocus
            aria-label="Contanti in cassa oggi, in euro"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') salva(); if (e.key === 'Escape') setEditing(false); }}
          />
          <button className="btn btn-sm btn-primary btn-icon" aria-label="Salva i contanti" title="Salva i contanti" onClick={salva}>
            <Icon name="check" size={12} />
          </button>
          <button className="btn btn-sm btn-ghost btn-icon" aria-label="Lascia i contanti come sono" title="Lascia i contanti come sono" onClick={() => setEditing(false)}>
            <Icon name="x" size={12} />
          </button>
        </div>
      ) : (
        <div className="st-value"><span className="u">€</span><AnimatedNumber value={saldo} format={fmtSaldo} /></div>
      )}
      <div className="st-delta">
        {canWrite && !editing ? (
          <button className="btn btn-sm btn-ghost" disabled={correggiContanti.isPending} onClick={apri} title="Scrivi quanti contanti ci sono in cassa oggi">
            <Icon name="pencil" size={10} /> Correggi i contanti
          </button>
        ) : editing ? 'quanti ce ne sono oggi, contati a mano' : 'si contano a mano, non da una banca'}
      </div>
    </div>
  );
}
